import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, Save, Send, Table2, Search, CheckCircle2, Plus, Pencil, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const curMonth = () => new Date().toISOString().slice(0, 7);
const fmt = (v) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

const Grid = ({ sheet, rows, setRows, q, onDelField }) => {
  const fields = sheet.fields;
  const shown = rows.filter((r) => !q || `${r.name} ${r.iqama} ${r.employee_number}`.toLowerCase().includes(q.toLowerCase()));
  const setVal = (id, k, v) => setRows((rs) => rs.map((r) => (r.employee_id === id ? { ...r, values: { ...r.values, [k]: v }, dirty: true } : r)));
  const net = (r) => fields.reduce((a, f) => a + (f.type === "deduction" ? -1 : 1) * (Number(r.values?.[f.key]) || 0), 0);
  return (
    <div className="max-h-[65vh] overflow-auto rounded-2xl border border-slate-200 bg-white" data-testid="manual-grid">
      <table className="min-w-max text-xs">
        <thead className="sticky top-0 z-10 bg-slate-100 text-slate-600">
          <tr>
            <th className="sticky right-0 z-20 min-w-[180px] bg-slate-100 px-2 py-2 text-start">الموظف</th>
            <th className="px-2 py-2">رقم الإقامة</th>
            {fields.map((f) => (
              <th key={f.key} className={`px-2 py-2 ${f.type === "deduction" ? "text-rose-600" : "text-emerald-700"}`}>
                <span className="inline-flex items-center gap-1">{f.label}
                  <button type="button" onClick={() => onDelField(f)} title="حذف البند" className="rounded p-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`manual-col-del-${f.key}`}><X className="h-3 w-3" /></button>
                </span>
              </th>
            ))}
            <th className="px-2 py-2 text-blue-700">الصافي</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.employee_id} className={`border-t border-slate-100 ${r.dirty ? "bg-amber-50/50" : ""}`}>
              <td className="sticky right-0 bg-white px-2 py-1 font-semibold text-slate-700">{r.name}</td>
              <td className="px-2 py-1 text-slate-500" dir="ltr">{r.iqama || "—"}</td>
              {fields.map((f) => (
                <td key={f.key} className="p-0.5">
                  <Input type="number" value={r.values?.[f.key] || ""} onChange={(e) => setVal(r.employee_id, f.key, e.target.value)}
                    className="h-8 w-24 text-xs" data-testid={`manual-cell-${r.employee_id}-${f.key}`} />
                </td>
              ))}
              <td className="px-2 py-1 font-bold tabular-nums text-blue-700" data-testid={`manual-net-${r.employee_id}`}>{fmt(net(r))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TplBuilder = ({ tpl, onClose, onSaved }) => {
  const [name, setName] = useState(tpl?.name || "");
  const [rows, setRows] = useState(tpl?.fields?.length ? tpl.fields.map((f) => ({ ...f })) : [{ label: "", type: "allowance" }, { label: "", type: "deduction" }]);
  const [busy, setBusy] = useState(false);
  const set = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    const fields = rows.filter((r) => r.label.trim());
    if (!name.trim() || !fields.length) { toast.warning("اكتب اسم القالب وبنداً واحداً على الأقل"); return; }
    setBusy(true);
    try {
      const d = tpl?.id ? (await api.updateSalaryTemplate(tpl.id, name.trim(), fields), { id: tpl.id }) : await api.createSalaryTemplate(name.trim(), fields);
      toast.success("تم حفظ القالب"); onSaved(d.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="manual-tpl-builder">
        <DialogHeader className="text-start"><DialogTitle>{tpl?.id ? "تعديل القالب" : "إنشاء قالب رواتب جديد"}</DialogTitle>
          <DialogDescription className="text-start">كل بند يصبح عموداً في الكشف وفي ملف الإكسل. البدلات تُجمع والخصومات تُطرح لحساب الصافي.</DialogDescription></DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم القالب (مثال: رواتب الإداريين)" data-testid="manual-tpl-name" />
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={r.label} onChange={(e) => set(i, "label", e.target.value)} placeholder={`اسم البند ${i + 1}`} className="h-9 flex-1" data-testid={`manual-tpl-label-${i}`} />
              <select value={r.type} onChange={(e) => set(i, "type", e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`manual-tpl-type-${i}`}>
                <option value="allowance">بدل / مستحق (+)</option><option value="deduction">خصم (−)</option>
              </select>
              <button type="button" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`manual-tpl-del-${i}`}><X className="h-4 w-4" /></button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setRows((x) => [...x, { label: "", type: "allowance" }])} className="gap-1" data-testid="manual-tpl-add-row"><Plus className="h-4 w-4" /> إضافة بند</Button>
        </div>
        <Button onClick={save} disabled={busy} className="w-full gap-1 bg-blue-600 hover:bg-blue-700" data-testid="manual-tpl-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ القالب</Button>
      </DialogContent>
    </Dialog>
  );
};

export const SalaryManualTab = () => {
  const [month, setMonth] = useState(curMonth());
  const [tplId, setTplId] = useState("standard");
  const [tpls, setTpls] = useState([]);
  const [sheet, setSheet] = useState(undefined);
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState("");
  const [busy, setBusy] = useState("");
  const [q, setQ] = useState("");
  const [builder, setBuilder] = useState(null);
  const ref = useRef();
  const loadTpls = () => api.salaryTemplates().then((d) => setTpls(d.templates || [])).catch(() => {});
  const curTpl = tpls.find((t) => t.id === tplId);
  const setFields = (fields, msg) => run("fields", async () => {
    if (dirty) await api.manualSave(month, rows);
    const d = await api.manualFields(month, fields); apply(d.sheet); toast.success(msg);
  });
  const delField = (f) => window.confirm(`حذف البند «${f.label}» من كشف هذا الشهر؟ (تُحذف قيمه أيضاً)`) && setFields(sheet.fields.filter((x) => x.key !== f.key), `تم حذف «${f.label}»`);
  const [nf, setNf] = useState({ label: "", type: "allowance" });
  const addField = () => {
    if (!nf.label.trim()) { toast.warning("اكتب اسم البند"); return; }
    setFields([...sheet.fields, { label: nf.label.trim(), type: nf.type }], `تمت إضافة «${nf.label.trim()}»`);
    setNf({ label: "", type: "allowance" });
  };
  const delTpl = async () => {
    if (!curTpl || curTpl.builtin || !window.confirm(`حذف القالب «${curTpl.name}»؟`)) return;
    try { await api.deleteSalaryTemplate(curTpl.id); setTplId("standard"); loadTpls(); toast.success("تم حذف القالب"); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };

  const apply = (s) => { setSheet(s); setRows((s?.rows || []).map((r) => ({ ...r }))); if (s?.template_id) setTplId(s.template_id); };
  const load = useCallback(() => { setSheet(undefined); api.manualSheet(month).then((d) => apply(d.sheet)).catch(() => apply(null)); }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTpls(); }, []);
  const dirty = useMemo(() => rows.some((r) => r.dirty), [rows]);

  const run = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تنفيذ العملية"); } finally { setBusy(""); } };
  const adopt = () => run("adopt", async () => { const d = await api.manualAdopt(month, tplId); apply(d.sheet); toast.success(`تم اعتماد الكشف اليدوي لشهر ${month} — ${d.sheet.rows.length} موظف`); });
  const save = () => run("save", async () => { const d = await api.manualSave(month, rows); apply(d.sheet); toast.success("تم الحفظ"); });
  const upload = (f) => f && run("upload", async () => { const d = await api.manualUpload(month, f); apply(d.sheet); toast.success(`تم تعبئة ${d.updated} موظف من الملف${d.not_found.length ? ` · ${d.not_found.length} سطر غير معروف` : ""}`); if (ref.current) ref.current.value = ""; });
  const post = () => run("post", async () => {
    if (dirty) await api.manualSave(month, rows);
    const d = await api.manualPost(month); toast.success(`تم ترحيل ${d.posted} راتب إلى كشف الرواتب (بانتظار الاعتماد)`); load();
  });
  const download = () => run("dl", async () => {
    const r = await fetch(api.manualTemplateUrl(month), { credentials: "include" });
    if (!r.ok) throw new Error();
    const url = URL.createObjectURL(await r.blob()); const a = document.createElement("a");
    a.href = url; a.download = `كشف_رواتب_يدوي_${month}.xlsx`; a.click(); URL.revokeObjectURL(url);
  });

  return (
    <div className="space-y-3" data-testid="salary-manual-tab">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" data-testid="manual-month" />
        <select value={tplId} onChange={(e) => setTplId(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="manual-template-select">
          {tpls.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <Button size="sm" onClick={adopt} disabled={!!busy} className="gap-1 bg-blue-600 hover:bg-blue-700" data-testid="manual-adopt-btn">
          {busy === "adopt" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          {!sheet ? "اعتماد الكشف اليدوي لهذا الشهر" : sheet.template_id !== tplId ? "اعتماد القالب المختار لهذا الشهر" : "تحديث أسماء الموظفين"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setBuilder({})} className="gap-1 border-blue-300 text-blue-700" data-testid="manual-tpl-new"><Plus className="h-4 w-4" /> إنشاء قالب جديد</Button>
        {curTpl && !curTpl.builtin && <>
          <Button size="icon" variant="ghost" onClick={() => setBuilder(curTpl)} title="تعديل القالب" data-testid="manual-tpl-edit"><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" onClick={delTpl} title="حذف القالب" className="text-rose-600" data-testid="manual-tpl-delete"><Trash2 className="h-4 w-4" /></Button>
        </>}
        <p className="text-xs text-slate-500">عند الاعتماد تُسحب أسماء الموظفين النشطين وإقاماتهم تلقائياً، والقيم المعبأة سابقاً تبقى.</p>
      </div>

      {builder && <TplBuilder tpl={builder} onClose={() => setBuilder(null)} onSaved={(id) => { setBuilder(null); loadTpls(); setTplId(id); toast.info("اضغط «اعتماد القالب المختار» لتطبيقه على الشهر، ثم «تحميل القالب»"); }} />}
      {sheet === undefined ? <div className="py-10 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
        : !sheet ? <p className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="manual-empty">لا يوجد كشف يدوي معتمد لشهر {month}. اختر القالب واضغط «اعتماد الكشف اليدوي».</p>
          : (
            <>
              <div className="flex flex-wrap items-center gap-2" data-testid="manual-actions">
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700" data-testid="manual-info">{sheet.template_name} · {rows.length} موظف{sheet.posted_at ? " · تم الترحيل" : ""}</span>
                <Button size="sm" variant={mode === "grid" ? "default" : "outline"} onClick={() => setMode(mode === "grid" ? "" : "grid")} className="gap-1" data-testid="manual-mode-grid"><Table2 className="h-4 w-4" /> تعبئة يدوية (مثل الإكسل)</Button>
                <Button size="sm" variant="outline" onClick={download} disabled={!!busy} className="gap-1" data-testid="manual-template-download">{busy === "dl" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تحميل القالب</Button>
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100" data-testid="manual-upload">
                  {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} رفع القالب المعبأ
                  <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="manual-upload-file" />
                </label>
                <Button size="sm" onClick={post} disabled={!!busy} className="ms-auto gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="manual-post-btn">{busy === "post" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} ترحيل إلى كشف الرواتب</Button>
              </div>
              {mode === "grid" && (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative w-full max-w-xs"><Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو الإقامة" className="h-9 pr-9" data-testid="manual-search" /></div>
                    <Button size="sm" onClick={save} disabled={!dirty || !!busy} className="gap-1" data-testid="manual-save-btn">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ التعديلات</Button>
                    {dirty && <span className="text-xs text-amber-600" data-testid="manual-dirty">تعديلات غير محفوظة</span>}
                    <div className="ms-auto flex flex-wrap items-center gap-1 rounded-xl border border-dashed border-blue-300 bg-blue-50/40 p-1" data-testid="manual-add-field">
                      <Input value={nf.label} onChange={(e) => setNf({ ...nf, label: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addField()} placeholder="اسم بند جديد" className="h-8 w-40 bg-white text-xs" data-testid="manual-new-field-label" />
                      <select value={nf.type} onChange={(e) => setNf({ ...nf, type: e.target.value })} className="h-8 rounded-md border border-slate-200 bg-white px-1 text-xs" data-testid="manual-new-field-type">
                        <option value="allowance">بدل (+)</option><option value="deduction">خصم (−)</option>
                      </select>
                      <Button size="sm" onClick={addField} disabled={!!busy} className="h-8 gap-1 bg-blue-600 hover:bg-blue-700" data-testid="manual-new-field-add">{busy === "fields" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} إضافة بند</Button>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500">احذف أي بند بعلامة × بجانب اسمه في رأس الجدول. التعديل يخص كشف هذا الشهر، وملف الإكسل يُنزَّل بالبنود الجديدة.</p>
                  <Grid sheet={sheet} rows={rows} setRows={setRows} q={q} onDelField={delField} />
                </>
              )}
            </>
          )}
    </div>
  );
};
